/**
 * How a tab strip lays itself out for the width it has been given.
 *
 * `full`  every tab shows its label.
 * `icons` the labels go, every tab stays on the strip, tooltip per tab.
 * `more`  not even the icons fit, so the tail moves into a dropdown.
 *
 * See `docs/kb/decisions/2026-08-27-project-tabs-collapse-to-icons-then-more.md`.
 */
export type StripMode = "full" | "icons" | "more"

export interface StripMetrics {
  /** natural width of each tab, in tab order, label rendered */
  fullTabWidths: number[]
  /** natural width of each tab, in tab order, label collapsed away */
  iconTabWidths: number[]
  /**
   * Width of the "More" button for every label it can carry — the button takes
   * the open section's name when that section is demoted, so probing only the
   * label active at measure time under-reserves as soon as the user navigates.
   */
  moreWidths: Record<string, number>
  /** the strip's own horizontal padding, excluded from every comparison */
  padding: number
  /** the gap between the tab group and the "More" button */
  gap: number
}

export interface StripLayout<Key extends string> {
  mode: StripMode
  hiddenKeys: Set<Key>
}

/** Never collapse the strip to nothing, however narrow the column gets. */
const MIN_VISIBLE_TABS = 1

/**
 * Reserve for the "More" button when its probe cannot be measured. Generous on
 * purpose: under-reserving lets the button overflow, which is the reported bug.
 */
const MORE_CHEVRON_ALLOWANCE = 24

const sum = (widths: number[]) =>
  widths.reduce((total, width) => total + width, 0)

interface ResolveArgs<Key extends string> {
  tabs: Array<{ value: Key; label: string }>
  activeTab: Key | null
  /** null until the strip has been measured */
  metrics: StripMetrics | null
  /** the strip's `clientWidth`, null until it has been observed */
  available: number | null
  /** what the button says while the open section is still on the strip */
  moreLabel: string
}

/**
 * Pure, which is what keeps the ladder stable: deriving the mode from a live
 * "is it overflowing?" read collapses the strip, which removes the overflow,
 * which re-expands it, forever.
 */
export function resolveStripLayout<Key extends string>({
  tabs,
  activeTab,
  metrics,
  available,
  moreLabel
}: ResolveArgs<Key>): StripLayout<Key> {
  const none = new Set<Key>()

  if (metrics === null || available === null) {
    return { mode: "full", hiddenKeys: none }
  }

  // Every comparison is against the content box, so padding comes off once here.
  const content = available - metrics.padding

  if (sum(metrics.fullTabWidths) <= content) {
    return { mode: "full", hiddenKeys: none }
  }
  if (sum(metrics.iconTabWidths) <= content) {
    return { mode: "icons", hiddenKeys: none }
  }

  const fallbackReserve =
    Math.max(0, ...metrics.iconTabWidths) + MORE_CHEVRON_ALLOWANCE

  // The button sits beside the tab group, so it costs its width plus the gap.
  const reserveFor = (label: string) =>
    (metrics.moreWidths[label] || fallbackReserve) + metrics.gap

  const hideUntilItFits = (reserve: number) => {
    const hidden = new Set<Key>()
    let used = sum(metrics.iconTabWidths)

    for (
      let i = tabs.length - 1;
      i >= MIN_VISIBLE_TABS && used + reserve > content;
      i--
    ) {
      used -= metrics.iconTabWidths[i]
      hidden.add(tabs[i].value)
    }

    return hidden
  }

  // Which label the button carries depends on the hidden set, so settle it in
  // two passes rather than guessing.
  const plainReserve = reserveFor(moreLabel)
  const hiddenKeys = hideUntilItFits(plainReserve)
  const activeLabel = tabs.find((tab) => tab.value === activeTab)?.label

  if (activeTab !== null && activeLabel && hiddenKeys.has(activeTab)) {
    const widened = reserveFor(activeLabel)
    if (widened > plainReserve) {
      return { mode: "more", hiddenKeys: hideUntilItFits(widened) }
    }
  }

  return { mode: "more", hiddenKeys }
}
